import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Heart, Minus, Plus, Ruler } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { useCatalog } from "@/data/catalog";

const Accordion = ({ title, children, id, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-stone/10">
      <button
        data-testid={`accordion-${id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left font-display uppercase tracking-[0.15em] text-sm hover:text-mist transition-colors"
        aria-expanded={open}
      >
        {title}
        <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-6 text-sm text-stone/60 leading-relaxed">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SizeGuide = () => (
  <table className="w-full text-xs mt-2 border-collapse">
    <thead>
      <tr className="text-stone/40 uppercase tracking-wider">
        <th className="text-left py-2 font-normal">Size</th>
        <th className="text-left py-2 font-normal">Chest (in)</th>
        <th className="text-left py-2 font-normal">Waist (in)</th>
      </tr>
    </thead>
    <tbody className="text-stone/70">
      {[["XS", "32–34", "26–28"], ["S", "35–37", "29–31"], ["M", "38–40", "32–34"], ["L", "41–43", "35–37"], ["XL", "44–46", "38–40"], ["XXL", "47–49", "41–43"]].map((r) => (
        <tr key={r[0]} className="border-t border-stone/10">
          <td className="py-2">{r[0]}</td>
          <td className="py-2">{r[1]}</td>
          <td className="py-2">{r[2]}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function ProductPage() {
  const { id } = useParams();
  const { getProduct, products: PRODUCTS, loading } = useCatalog();
  const product = getProduct(id);
  const { addItem, wishlist, toggleWishlist } = useCart();
  const [image, setImage] = useState(0);
  const [color, setColor] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    setImage(0);
    setColor(0);
    setSize(null);
    setQty(1);
  }, [id]);

  const related = useMemo(
    () => (product ? PRODUCTS.filter((p) => p.id !== product.id && (p.collection === product.collection || p.category === product.category)).slice(0, 4) : []),
    [product, PRODUCTS]
  );

  if (loading && !product) {
    return (
      <main data-testid="product-loading" className="pt-40 pb-32 text-center px-6">
        <p className="text-xs tracking-[0.3em] uppercase text-stone/40">Loading…</p>
      </main>
    );
  }

  if (!product) {
    return (
      <main data-testid="product-not-found" className="pt-40 pb-32 text-center px-6">
        <h1 className="font-display text-4xl uppercase">Product not found</h1>
        <Link to="/shop" className="inline-block mt-8 border border-stone/30 px-8 py-3 font-display uppercase tracking-[0.2em] text-sm hover:bg-stone hover:text-obsidian transition-colors">
          Back to shop
        </Link>
      </main>
    );
  }

  const wished = wishlist.includes(product.id);

  const addToBag = () => {
    if (!size) {
      toast.error("Select a size first.");
      return;
    }
    addItem(product, size, product.colors[color].name, qty);
    toast.success(`${product.name} added to your bag.`);
  };

  return (
    <main data-testid="product-page" className="pt-24 md:pt-32 pb-32 lg:pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <nav className="text-[11px] tracking-[0.2em] uppercase text-stone/40 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-stone">Home</Link> / <Link to="/shop" className="hover:text-stone">Shop</Link> /{" "}
          <span className="text-stone/70">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          <div className="min-w-0">
            <div className="relative aspect-[2/3] lg:aspect-[3/4] overflow-hidden bg-deep grain">
              <AnimatePresence mode="wait">
                <motion.img
                  key={image}
                  src={product.images[image]}
                  alt={`${product.name} — view ${image + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                />
              </AnimatePresence>
              {product.tag && (
                <span className="absolute top-4 left-4 text-[10px] font-semibold tracking-[0.2em] uppercase px-2.5 py-1 bg-stone text-obsidian">
                  {product.tag === "new" ? "New" : "Best Seller"}
                </span>
              )}
            </div>
            <div className="flex gap-3 mt-4 overflow-x-auto pb-2 [scrollbar-width:thin]">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setImage(i)}
                  className={`w-20 aspect-[3/4] overflow-hidden border transition-colors ${
                    image === i ? "border-mist" : "border-stone/15 hover:border-stone/40"
                  }`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <FadeUp className="min-w-0">
            <p className="text-xs tracking-[0.3em] uppercase text-mist">
              {product.category} / {product.collection}
            </p>
            <h1 data-testid="product-name" className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight mt-3 leading-[1.02]">
              {product.name}
            </h1>
            <p data-testid="product-price" className="font-display text-2xl text-mist mt-5">
              {formatPrice(product.price)}
            </p>
            <p className="text-stone/60 mt-6 leading-relaxed max-w-lg">{product.description}</p>

            <div className="mt-9">
              <p className="text-xs tracking-[0.25em] uppercase text-stone/40 mb-3">
                Color — <span className="text-stone/70">{product.colors[color].name}</span>
              </p>
              <div className="flex gap-3">
                {product.colors.map((c, i) => (
                  <button
                    key={c.name}
                    data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setColor(i)}
                    className={`h-9 w-9 rounded-full border-2 transition-all ${
                      color === i ? "border-mist scale-110" : "border-stone/25 hover:border-stone/60"
                    }`}
                    style={{ backgroundColor: c.hex }}
                    aria-label={`Color ${c.name}`}
                  />
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs tracking-[0.25em] uppercase text-stone/40">Size</p>
                <button
                  data-testid="size-guide-button"
                  onClick={() => setGuideOpen(!guideOpen)}
                  className="text-xs tracking-[0.15em] uppercase text-mist inline-flex items-center gap-1.5 hover:text-stone transition-colors"
                >
                  <Ruler size={13} /> Size guide
                </button>
              </div>
              <AnimatePresence>
                {guideOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <SizeGuide />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-button-${s.toLowerCase()}`}
                    onClick={() => setSize(s)}
                    className={`min-w-12 px-3 py-3 border font-display tracking-widest text-sm transition-colors ${
                      size === s
                        ? "bg-stone text-obsidian border-stone"
                        : "border-stone/20 text-stone/70 hover:border-stone/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-9 space-y-3">
              <div className="flex gap-3">
                <div className="flex items-center justify-between border border-stone/20 flex-1 sm:flex-none">
                  <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="px-5 py-3.5 text-stone/70 hover:text-stone" aria-label="Decrease quantity">
                    <Minus size={15} />
                  </button>
                  <span data-testid="qty-value" className="px-3 font-display">{qty}</span>
                  <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="px-5 py-3.5 text-stone/70 hover:text-stone" aria-label="Increase quantity">
                    <Plus size={15} />
                  </button>
                </div>
                <button
                  data-testid="product-wishlist-button"
                  onClick={() => toggleWishlist(product.id)}
                  className="border border-stone/20 px-4 text-stone/70 hover:text-mist hover:border-mist transition-colors"
                  aria-label="Add to wishlist"
                >
                  <Heart size={18} className={wished ? "fill-mist text-mist" : ""} />
                </button>
              </div>
              <button
                data-testid="add-to-cart-button"
                onClick={addToBag}
                className="btn-slide w-full bg-stone text-obsidian font-display uppercase tracking-[0.2em] text-sm py-4 hover:bg-mist transition-colors"
              >
                <span className="btn-label">
                  <span className="sm:hidden">Add to Bag</span>
                  <span className="hidden sm:inline">Add to Bag — {formatPrice(product.price * qty)}</span>
                </span>
                <span className="btn-label-dup">Beyond Your Limits</span>
              </button>
            </div>

            <div className="mt-10 border-t border-stone/10">
              <Accordion title="Details" id="details" defaultOpen>
                <ul className="list-disc list-inside space-y-1.5">
                  {product.details.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </Accordion>
              <Accordion title="Shipping" id="shipping">
                Orders ship within 1–2 business days. Free standard shipping on orders over $100. Express options
                available at checkout.
              </Accordion>
              <Accordion title="Returns" id="returns">
                30-day returns on unworn items with tags attached. Start a return from your account or contact the
                MVM team.
              </Accordion>
            </div>
          </FadeUp>
        </div>

        {related.length > 0 && (
          <section data-testid="related-products" className="mt-28">
            <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mb-12">
              Complete the kit
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky buy bar — mobile only, purchase control always reachable */}
      <div
        data-testid="mobile-buy-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-[60] bg-obsidian/95 backdrop-blur-xl border-t border-stone/10 px-4 py-3 flex items-center gap-3"
      >
        <img src={product.images[0]} alt="" className="w-10 h-12 object-cover" />
        <div className="flex-1 min-w-0">
          <p className="font-display uppercase tracking-wide text-sm truncate">{product.name}</p>
          <p className="text-mist text-sm">{formatPrice(product.price * qty)}</p>
        </div>
        <button
          data-testid="mobile-add-to-cart-button"
          onClick={addToBag}
          className="bg-stone text-obsidian font-display uppercase tracking-[0.15em] text-xs px-6 py-3.5 hover:bg-mist transition-colors"
        >
          Add to Bag
        </button>
      </div>
    </main>
  );
}
